"use client";

import React, { useRef, useEffect } from 'react';
import { cn } from "@/lib/utils";
import { User, ChevronRight, LogOut } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useSession, signIn, signOut } from 'next-auth/react';
import Image from 'next/image';
import { motion } from 'framer-motion';

interface UserNavProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

const UserNav: React.FC<UserNavProps> = ({ isOpen, onOpenChange }) => {
  const router = useRouter();
  const { data: session, status } = useSession();
  const isLoading = status === "loading";
  const dialogRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!dialogRef.current?.contains(event.target as Node) &&
          !buttonRef.current?.contains(event.target as Node)) {
        onOpenChange(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onOpenChange]);

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={() => session ? onOpenChange(!isOpen) : signIn('google')}
        className="group flex items-center gap-2"
      >
        {session?.user?.image ? (
          <div className={cn(
            "w-4 h-4 rounded-full overflow-hidden",
            "border border-white/10",
            "transition-transform duration-200",
            "group-hover:scale-110"
          )}>
            <Image
              src={session.user.image}
              alt="Profile"
              width={16}
              height={16}
              className="object-cover"
            />
          </div>
        ) : (
          <User className={cn(
            "w-4 h-4",
            "text-gray-600 dark:text-gray-300",
            "transition-all duration-200",
            "group-hover:text-gray-900 dark:group-hover:text-white",
            "group-hover:scale-110"
          )} />
        )}
        <span className={cn(
          "text-sm",
          "text-gray-600 dark:text-gray-300",
          "transition-all duration-200",
          "group-hover:text-gray-900 dark:group-hover:text-white",
          "hidden md:inline"
        )}>
          {isLoading ? 'Loading...' : session ? session.user?.name?.split(' ')[0] : 'Sign In'}
        </span>
      </button>

      {session && isOpen && (
        <>
          {/* Backdrop - position it below the topbar */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 top-16 z-30 bg-black/20 backdrop-blur-sm"
            onClick={() => onOpenChange(false)}
          />

          {/* Mobile Dialog - Full width */}
          <motion.div
            ref={dialogRef}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{
              height: { 
                type: "tween", 
                duration: 0.3,
                ease: [0.04, 0.62, 0.23, 0.98]
              },
              opacity: { duration: 0.2 }
            }}
            className={cn(
              "md:hidden", // Only show on mobile
              "fixed top-16 left-0 right-0",
              "bg-black/90",
              "backdrop-blur-2xl",
              "border-t border-white/10",
              "shadow-xl shadow-black/40",
              "overflow-hidden",
              "z-40"
            )}
          >
            <div className="mx-auto px-4 sm:px-6 max-w-7xl">
              {/* User Info Section */}
              <div className="py-4 flex items-center gap-3">
                {session.user?.image && (
                  <div className="w-10 h-10 rounded-full overflow-hidden border border-white/10">
                    <Image
                      src={session.user.image}
                      alt="Profile"
                      width={40}
                      height={40}
                      className="object-cover"
                    />
                  </div>
                )}
                <div>
                  <div className="text-sm text-white/90 font-medium">
                    {session.user?.name}
                  </div>
                  <div className="text-xs text-white/50">
                    {session.user?.email}
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="py-2 border-t border-white/10">
                <button
                  onClick={() => signOut()}
                  className={cn(
                    "w-full px-3 py-3 rounded-md",
                    "flex items-center gap-2",
                    "text-sm text-white/70",
                    "hover:bg-white/10",
                    "transition-colors duration-200"
                  )}
                >
                  <LogOut className="w-4 h-4" />
                  <span>Sign Out</span>
                </button>
              </div>
            </div>
          </motion.div>

          {/* Desktop Dialog - Aligned with right edge of page */}
          <motion.div
            ref={dialogRef}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{
              type: "spring",
              stiffness: 500,
              damping: 30,
              duration: 0.2
            }}
            className={cn(
              "hidden md:block", // Only show on desktop
              "fixed top-16 right-0",
              "w-64",
              "bg-black/90",
              "backdrop-blur-2xl",
              "border-t border-l border-white/10",
              "rounded-bl-lg",
              "shadow-xl shadow-black/40",
              "z-40",
              "overflow-hidden"
            )}
          >
            {/* User Info Section */}
            <div className="p-4 border-b border-white/10">
              <div className="flex items-center gap-3">
                {session.user?.image && (
                  <div className="w-10 h-10 rounded-full overflow-hidden border border-white/10">
                    <Image
                      src={session.user.image}
                      alt="Profile"
                      width={40}
                      height={40}
                      className="object-cover"
                    />
                  </div>
                )}
                <div>
                  <div className="text-sm text-white/90 font-medium">
                    {session.user?.name}
                  </div>
                  <div className="text-xs text-white/50">
                    {session.user?.email}
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="p-2">
              <button
                onClick={() => signOut()}
                className={cn(
                  "w-full px-3 py-2 rounded-md",
                  "flex items-center gap-2",
                  "text-xs text-white/70",
                  "hover:bg-white/10",
                  "transition-colors duration-200"
                )}
              >
                <LogOut className="w-4 h-4" />
                <span>Sign Out</span>
              </button>
            </div>
          </motion.div>
        </>
      )}
    </div>
  );
};

export default UserNav;